import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface SubscriptionState {
  tenantId: string | null;
  tenantName: string | null;
  plan: string;
  /** true once a paid plan is active */
  isSubscribed: boolean;
  trialEndsAt: Date | null;
  /** whole days remaining in the trial (0 when expired/unknown) */
  daysLeft: number;
  isOnTrial: boolean;
  isTrialExpired: boolean;
  unitCount: number;
  isLoading: boolean;
}

const PAID_PLANS = ['starter', 'growth', 'pro', 'enterprise'];

export const useSubscription = (): SubscriptionState => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['subscription-state', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('tenant_id, tenants!inner(id, name, plan, trial_ends_at)')
        .eq('user_id', user.id)
        .maybeSingle();

      const tenant = (roleRow as any)?.tenants ?? null;
      if (!tenant) return null;

      const { count } = await supabase
        .from('units')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id);

      return { tenant, unitCount: count || 0 };
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const tenant = data?.tenant ?? null;
  const plan: string = tenant?.plan ?? 'free_trial';
  const isSubscribed = PAID_PLANS.includes(plan);
  const trialEndsAt = tenant?.trial_ends_at ? new Date(tenant.trial_ends_at) : null;

  const msLeft = trialEndsAt ? trialEndsAt.getTime() - Date.now() : 0;
  const daysLeft = trialEndsAt ? Math.max(0, Math.ceil(msLeft / 86_400_000)) : 0;

  return {
    tenantId: tenant?.id ?? null,
    tenantName: tenant?.name ?? null,
    plan,
    isSubscribed,
    trialEndsAt,
    daysLeft,
    isOnTrial: !isSubscribed && msLeft > 0,
    isTrialExpired: !isSubscribed && !!trialEndsAt && msLeft <= 0,
    unitCount: data?.unitCount ?? 0,
    isLoading,
  };
};
